import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ntd } from '@/lib/format';
import {
  Card,
  Empty,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  thStyle,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { CustomerHoldingsModal } from '@/components/CustomerHoldingsModal';

interface Recall {
  days: number;
  dormantDays: number;
  expiringTickets: {
    customerId: string;
    customerName: string;
    phone: string;
    ownerName: string | null;
    treatmentName: string;
    remaining: number;
    total: number;
    expireAt: string;
    daysLeft: number;
  }[];
  dormantWallets: {
    customerId: string;
    customerName: string;
    phone: string;
    ownerName: string | null;
    balanceCents: number;
    lastVisitAt: string | null;
    daysSinceVisit: number | null;
  }[];
}

type Sel = { id: string; name: string; phone?: string } | null;

export function RecallPage() {
  const [days, setDays] = useState(30);
  const [detail, setDetail] = useState<Sel>(null);

  const q = useQuery({
    queryKey: ['recall', days],
    queryFn: () => api.get<Recall>(`/engagement/recall?days=${days}`),
  });

  const tickets = q.data?.expiringTickets ?? [];
  const wallets = q.data?.dormantWallets ?? [];
  const dormantTotal = wallets.reduce((s, w) => s + w.balanceCents, 0);

  const nameBtn = (c: Sel) => (
    <button
      onClick={() => setDetail(c)}
      style={{ background: 'transparent', border: 0, padding: 0, font: 'inherit', fontWeight: 600, color: 'var(--primary-deep, var(--ink))', cursor: 'pointer' }}
    >
      {c!.name}
    </button>
  );

  return (
    <div>
      <PageHeader
        title="到期喚回名單"
        subtitle="把躺在系統裡的錢叫回來:即將到期的堂票 + 沉睡的儲值金"
        right={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>到期前</span>
            <Select
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              options={[14, 30, 60, 90].map((d) => ({ value: String(d), label: `${d} 天` }))}
              style={{ width: 110 }}
            />
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="即將到期堂票" value={`${tickets.length} 筆`} tone="ticket" hint={`${days} 天內`} />
        <Stat label="沉睡儲值金客數" value={`${wallets.length} 位`} tone="wallet" hint={`逾 ${q.data?.dormantDays ?? 90} 天未到訪`} />
        <Stat label="沉睡儲值金總額" value={ntd(dormantTotal)} tone="primary" hint="可喚回回購" />
      </div>

      <Card title="即將到期堂票" icon="⏳">
        {tickets.length === 0 ? (
          <Empty>{days} 天內無即將到期的堂票。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>顧客</th>
                <th style={thStyle}>電話</th>
                <th style={thStyle}>療程</th>
                <th style={thStyle}>剩餘</th>
                <th style={thStyle}>到期</th>
                <th style={thStyle}>主責</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t, i) => (
                <tr key={`${t.customerId}-${i}`}>
                  <td style={cellStyle}>{nameBtn({ id: t.customerId, name: t.customerName, phone: t.phone })}</td>
                  <td style={cellStyle} className="num">{t.phone}</td>
                  <td style={cellStyle}>{t.treatmentName}</td>
                  <td style={cellStyle} className="num"><Tag tone="ticket">{t.remaining}/{t.total}</Tag></td>
                  <td style={cellStyle} className="num">
                    <Tag tone={t.daysLeft <= 7 ? 'danger' : t.daysLeft <= 14 ? 'warn' : 'default'}>剩 {t.daysLeft} 天</Tag>
                  </td>
                  <td style={cellStyle}>{t.ownerName ?? '未指派'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="沉睡儲值金" icon="💤">
        {wallets.length === 0 ? (
          <Empty>無沉睡儲值金。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>顧客</th>
                <th style={thStyle}>電話</th>
                <th style={thStyle}>餘額</th>
                <th style={thStyle}>未到訪</th>
                <th style={thStyle}>主責</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map((w) => (
                <tr key={w.customerId}>
                  <td style={cellStyle}>{nameBtn({ id: w.customerId, name: w.customerName, phone: w.phone })}</td>
                  <td style={cellStyle} className="num">{w.phone}</td>
                  <td style={cellStyle} className="num"><Tag tone="wallet">{ntd(w.balanceCents)}</Tag></td>
                  <td style={cellStyle} className="num">{w.daysSinceVisit != null ? `${w.daysSinceVisit} 天` : '從未'}</td>
                  <td style={cellStyle}>{w.ownerName ?? '未指派'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <CustomerHoldingsModal customer={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
